import { useCallback, useEffect, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { AppBadge, AppButton, AppCard } from "../../../shared/components/ui";
import { AppDataTable } from "../../../shared/components/AppDataTable";
import { PageHeader, PageState } from "../../../shared/components/PageChrome";
import { IconCheck, IconSpinner } from "../../../shared/components/icons";
import { api, ApiError } from "../../../shared/api/client";

type OverdueRow = {
  contract_id: number;
  contract_number: string | null;
  customer_id: number | null;
  customer_name: string | null;
  phone: string | null;
  balance: number;
  days_overdue: number | null;
};

type OverdueResponse = {
  contracts?: Array<Record<string, unknown>>;
  overdue?: Array<Record<string, unknown>>;
};

type ArrearsResponse = {
  contracts: Array<Record<string, unknown>>;
};

function toRow(raw: Record<string, unknown>): OverdueRow {
  return {
    contract_id: Number(raw.contract_id ?? raw.id),
    contract_number: (raw.contract_number as string | undefined) ?? null,
    customer_id: raw.customer_id != null ? Number(raw.customer_id) : null,
    customer_name: (raw.customer_name as string | undefined) ?? null,
    phone: (raw.phone as string | undefined) ?? (raw.customer_phone as string | undefined) ?? null,
    balance: Number(raw.balance ?? raw.amount_remaining ?? raw.arrears ?? 0),
    days_overdue: raw.days_overdue != null ? Number(raw.days_overdue) : null,
  };
}

export function OverdueReminders() {
  const [rows, setRows] = useState<OverdueRow[]>([]);
  const [pageState, setPageState] = useState<"loading" | "error" | "success">("loading");
  const [pageError, setPageError] = useState("");
  const [usingFallback, setUsingFallback] = useState(false);
  const [remindBusy, setRemindBusy] = useState<number | null>(null);
  const [remindedIds, setRemindedIds] = useState<Set<number>>(new Set());

  const load = useCallback(async () => {
    setPageState("loading");
    setUsingFallback(false);
    try {
      const data = await api.get<OverdueResponse>("/contracts/overdue");
      const list = data.contracts ?? data.overdue ?? [];
      setRows(list.map(toRow));
      setPageState("success");
    } catch (ex: unknown) {
      if (ex instanceof ApiError && ex.status === 404) {
        try {
          const fallback = await api.get<ArrearsResponse>("/evaluator/arrears");
          setRows((fallback.contracts ?? []).map(toRow));
          setUsingFallback(true);
          setPageState("success");
        } catch (fallbackEx: unknown) {
          setPageError(fallbackEx instanceof Error ? fallbackEx.message : "Failed to load overdue contracts");
          setPageState("error");
        }
        return;
      }
      setPageError(ex instanceof Error ? ex.message : "Failed to load overdue contracts");
      setPageState("error");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendReminder = async (contractId: number) => {
    setRemindBusy(contractId);
    try {
      await api.post(`/contracts/${contractId}/remind`, {});
      setRemindedIds((prev) => new Set(prev).add(contractId));
    } catch {
      /* leave the row actionable so the office can retry */
    } finally {
      setRemindBusy(null);
    }
  };

  const columns: ColumnDef<OverdueRow, unknown>[] = [
    { id: "contract", header: "Contract", accessorFn: (r) => r.contract_number ?? `#${r.contract_id}` },
    { id: "customer", header: "Customer", accessorFn: (r) => r.customer_name ?? (r.customer_id ? `#${r.customer_id}` : "—") },
    { id: "phone", header: "Phone", accessorFn: (r) => r.phone ?? "—" },
    { id: "balance", header: "Balance due", accessorFn: (r) => `TZS ${r.balance.toLocaleString("en-US")}` },
    { id: "days_overdue", header: "Days overdue", accessorFn: (r) => r.days_overdue ?? "—" },
    {
      id: "actions", header: "", enableSorting: false,
      cell: ({ row }) => (
        remindedIds.has(row.original.contract_id) ? (
          <AppBadge tone="success"><IconCheck size={12} /> Reminder sent</AppBadge>
        ) : (
          <AppButton
            type="button" variant="secondary"
            disabled={remindBusy === row.original.contract_id}
            onClick={(e) => { e.stopPropagation(); sendReminder(row.original.contract_id); }}
          >
            {remindBusy === row.original.contract_id ? <IconSpinner size={12} /> : "Send reminder"}
          </AppButton>
        )
      ),
    },
  ];

  const listStatus =
    pageState === "success" && rows.length === 0 ? "empty" : pageState;

  return (
    <div>
      <PageHeader
        title="Overdue Reminders"
        subtitle="Contracts behind schedule — send SMS payment reminders."
      />

      {usingFallback && pageState === "success" && (
        <p className="pg-text-muted">Showing arrears summary. Some columns may be unavailable.</p>
      )}

      <PageState
        status={listStatus}
        error={pageError}
        onRetry={load}
        emptyTitle="Nothing overdue"
        emptyHint="All contracts are current on their payment schedule."
      >
        <AppCard>
          <div className="pg-table-meta">
            <span>{rows.length} contract{rows.length !== 1 ? "s" : ""} overdue</span>
          </div>
          <AppDataTable data={rows} columns={columns} />
        </AppCard>
      </PageState>
    </div>
  );
}
